import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  applyChatPositionOverrides,
  fetchChatbotScript,
  isSprinklrChatLoaded,
  loadSprinklrChatWidget,
  publishSprinklrChatStatus,
  startSprinklrPositionObserver,
  stopSprinklrPositionObserver,
  waitForSprinklrLauncher,
} from '../../utils/sprinklrChat'
import './globalChatWidget.scss'

const GlobalChatWidget = () => {
  const location = useLocation()
  const isLoadingRef = useRef(false)
  const {
    Language: { selectedLanguage },
  } = useSelector((state) => state)

  const initializeChatWidget = useCallback(async (trigger = 'mount') => {
    if (isLoadingRef.current || isSprinklrChatLoaded()) {
      if (isSprinklrChatLoaded()) {
        applyChatPositionOverrides()
        publishSprinklrChatStatus({
          state: 'ready',
          trigger,
          source: 'existing',
          visible: !!document.querySelector('[class*="ezg1tqb0"]'),
        })
      }
      return
    }

    isLoadingRef.current = true
    publishSprinklrChatStatus({
      state: 'loading',
      trigger,
      source: null,
      visible: false,
    })

    try {
      const { script, source, error } = await fetchChatbotScript()
      if (!script) {
        publishSprinklrChatStatus({
          state: 'failed',
          trigger,
          source,
          visible: false,
          error: error || 'Chatbot script is unavailable before login',
        })
        return
      }

      const injected = loadSprinklrChatWidget(script)
      if (!injected) {
        publishSprinklrChatStatus({
          state: 'failed',
          trigger,
          source,
          visible: false,
          error: 'Failed to inject chatbot script',
        })
        return
      }

      applyChatPositionOverrides()
      const launcherResult = await waitForSprinklrLauncher()

      publishSprinklrChatStatus({
        state: launcherResult.ok ? 'ready' : 'failed',
        trigger,
        source,
        visible: !!launcherResult.element,
        error: launcherResult.ok ? null : launcherResult.reason,
      })

      if (launcherResult.ok) {
        applyChatPositionOverrides()
      }
    } finally {
      isLoadingRef.current = false
    }
  }, [])

  useEffect(() => {
    initializeChatWidget('route-change')
  }, [initializeChatWidget, location.pathname, location.search])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      applyChatPositionOverrides()
    }, 0)

    return () => {
      window.clearTimeout(timer)
    }
  }, [selectedLanguage])

  useEffect(() => {
    const dirLayout = document.getElementById('dirLayout')
    if (!dirLayout) {
      return undefined
    }

    const dirObserver = new MutationObserver(() => {
      applyChatPositionOverrides()
    })

    dirObserver.observe(dirLayout, {
      attributes: true,
      attributeFilter: ['dir'],
    })

    return () => {
      dirObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const reapplyPosition = (event) => {
      if (
        !event.target.closest(
          '[class*="ezg"], iframe[src*="sprinklr"], iframe[src*="live-chat"]'
        )
      ) {
        return
      }

      window.setTimeout(() => applyChatPositionOverrides(), 50)
      window.setTimeout(() => applyChatPositionOverrides(), 300)
      window.setTimeout(() => applyChatPositionOverrides(), 800)
    }

    document.addEventListener('click', reapplyPosition, true)
    return () => {
      document.removeEventListener('click', reapplyPosition, true)
    }
  }, [])

  useEffect(() => {
    startSprinklrPositionObserver()
    return () => {
      stopSprinklrPositionObserver()
    }
  }, [])

  useEffect(() => {
    const handleHashChange = () => {
      initializeChatWidget('hash-change')
    }

    window.addEventListener('hashchange', handleHashChange)
    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [initializeChatWidget])

  useEffect(() => {
    const handleStatusRequest = () => {
      applyChatPositionOverrides()
      publishSprinklrChatStatus({
        state: isSprinklrChatLoaded() ? 'ready' : 'failed',
        trigger: 'status-request',
        source: null,
        visible: !!document.querySelector('[class*="ezg1tqb0"]'),
      })
    }

    window.addEventListener('sprinklrChatStatusRequest', handleStatusRequest)
    return () => {
      window.removeEventListener('sprinklrChatStatusRequest', handleStatusRequest)
    }
  }, [])

  return null
}

export default GlobalChatWidget
